import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { action, computed, makeObservable, observable } from "mobx";
import { toastModule } from "@/modules/toast";
import pTimeout from "p-timeout";

const CONFIRM_TIMEOUT_MS = 15_000;
const CONFIRM_TIMEOUT_SENTINEL = Symbol("active-recording-conflict-timeout");

type ConflictModalState = {
  onConfirm: () => Promise<void> | void;
  onCancel?: () => void;
};

export class ActiveRecordingConflictModalStore extends AppSubStore {
  private state: ConflictModalState | null = null;
  private isConfirmingValue = false;
  private confirmPromise: Promise<void> | null = null;
  private confirmToken: symbol | null = null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    makeObservable<this, "state" | "isConfirmingValue" | "confirmPromise" | "confirmToken">(this, {
      state: observable.ref,
      isConfirmingValue: observable,
      confirmPromise: observable.ref,
      confirmToken: observable.ref,
      isOpen: computed,
      isConfirming: computed,
      isConfirmingUi: computed,
      open: action,
      close: action,
      handleConfirm: action,
      handleClose: action,
      title: computed,
      message: computed,
      confirmLabel: computed,
    });
  }

  get title(): string {
    return "Voice Mode is already in progress";
  }

  get message(): string {
    return "Do you want to end your current recording to start a new one?";
  }

  get confirmLabel(): string {
    return "Start New Recording";
  }

  get isOpen(): boolean {
    return Boolean(this.state);
  }

  get isConfirming(): boolean {
    return Boolean(this.confirmPromise);
  }

  get isConfirmingUi(): boolean {
    return this.isConfirmingValue;
  }

  open(state: ConflictModalState): void {
    this.state = state;
    this.isConfirmingValue = false;
  }

  close(): void {
    if (!this.isOpen) return;

    this.state = null;
    this.isConfirmingValue = false;
  }

  async handleConfirm(): Promise<void> {
    const currentState = this.state;
    if (!currentState) return;
    if (this.confirmPromise) return;

    this.isConfirmingValue = true;

    const confirmToken = Symbol("active-recording-conflict-confirm");
    this.confirmToken = confirmToken;

    const confirmPromise = Promise.resolve().then(() => currentState.onConfirm?.());
    this.confirmPromise = confirmPromise;

    try {
      void pTimeout(confirmPromise, {
        milliseconds: CONFIRM_TIMEOUT_MS,
        fallback: () => CONFIRM_TIMEOUT_SENTINEL,
      })
        .then(result => {
          if (result !== CONFIRM_TIMEOUT_SENTINEL) return;
          if (this.confirmToken !== confirmToken) return;
          if (this.confirmPromise !== confirmPromise) return;

          this.confirmPromise = null;
          this.isConfirmingValue = false;
          toastModule.triggerToast({
            content: "This is taking longer than expected. Please try again.",
            toastId: "active-recording-conflict-modal-error",
          });
        })
        .catch(() => {
          // Errors are handled by the main await below.
        });

      await confirmPromise;

      if (this.confirmToken !== confirmToken) return;
      if (this.state === currentState) {
        this.close();
      }
    } catch (unknownErr) {
      if (this.confirmToken !== confirmToken) return;
      logger.warn({
        message: "[ActiveRecordingConflictModalStore] handleConfirm failed",
        info: { error: objectModule.safeErrorAsJson(unknownErr as Error) },
      });

      toastModule.triggerToast({
        content: "Something went wrong. Please try again.",
        toastId: "active-recording-conflict-modal-error",
      });
    } finally {
      if (this.confirmToken === confirmToken) {
        this.confirmPromise = null;
        this.isConfirmingValue = false;
        this.confirmToken = null;
      }
    }
  }

  handleClose(): void {
    if (this.isConfirmingValue) return;
    const currentState = this.state;
    this.close();
    if (!this.confirmPromise) {
      currentState?.onCancel?.();
    }
  }
}
